import styled from '@emotion/styled'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { useLocation } from '@faceup/router'
import { useElementSize, useMediaQuery } from '@mantine/hooks'
import { type ReactNode, useContext, useEffect, useState } from 'react'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { breakpoints, desktopBreakpoint, maxAppWidth } from '../../utils/constants'
import useNetworkStatus from '../../utils/useNetworkStatus'

const messages = defineMessages('followUp.components.header', [
  { id: 'offlineBanner', defaultMessage: 'No internet connection' },
] as const)

type HeaderProps = { children: ReactNode }

export const Header = (props: HeaderProps) => {
  const { children } = props
  const [scrolling, setScrolling] = useState(false)
  const { client } = useContext(LayoutContext)
  const networkStatus = useNetworkStatus()
  const { pathname } = useLocation()
  const { ref, height } = useElementSize()
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints[desktopBreakpoint]})`, true)

  const isOfflineBannerVisible =
    client === 'app' && networkStatus === 'offline' && pathname !== '/offline'

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY <= 5 && scrolling) {
        setScrolling(false)
      } else if (window.scrollY > 5 && !scrolling) {
        setScrolling(true)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => window.removeEventListener('scroll', handleScroll)
  }, [scrolling])

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 105,
        maxWidth: maxAppWidth,
        width: '100%',
        margin: 'auto',
        background: 'transparent',
      }}
    >
      <div style={{ height, zIndex: 105 }}>
        <div />
      </div>
      <HeaderWrapper ref={ref} shadow={scrolling || !isDesktop}>
        <InnerHeader>{children}</InnerHeader>
        {isOfflineBannerVisible && (
          <OfflineBanner>
            <FormattedMessage message={messages.offlineBanner} />
          </OfflineBanner>
        )}
      </HeaderWrapper>
    </div>
  )
}

export const HeaderWrapper = styled('div', { shouldForwardProp: prop => prop !== 'shadow' })<{
  shadow?: boolean
}>`
  width: 100%;
  height: var(--followup-header-height);
  background-color: #fff;
  position: fixed;
  top: 0;
  left: 0;
  transition: 0.3s;
  z-index: 110;
  padding-top: var(--safe-area-inset-top);
  box-shadow: ${({ shadow }) => (shadow ? '0px 7px 7px rgba(0, 0, 0, 0.05) !important' : 'none')};
`

export const InnerHeader = styled.div`
  display: flex;
  flex-flow: nowrap row;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  position: relative;
  max-width: ${maxAppWidth}px;
  width: 100%;
  margin: auto;
  background: transparent;
  padding-inline: 24px;

  h4 {
    line-height: 34px;
  }
`

export const OfflineBanner = styled.div`
  background: #fef2f2;
  width: 100%;
  color: #ef4a45;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  justify-content: center;
  padding: 6px 20px;
`
